// The Simple Digital Playbook.
// Writing rules (kept here so any future contributor sees them):
//  - Write like a trusted friend, not a consultant.
//  - Never make people feel stupid. Instead of "you should already know this",
//    say "most people don't know this, and that's okay".
//  - Explain any technical term in plain English on first use, then stop using it.
//  - Every guide follows the same 8-step flow:
//      1. The Question
//      2. The Short Answer
//      3. Why This Matters
//      4. ☕ Over a Coffee (one-minute story, everyday scenario, no stats)
//      5. Let's Fix It
//      6. Do This Today (one action, 20 min or less)
//      7. Simple Summary
//      8. What's Next
//  - Every reader should finish thinking "I can actually do this."

export const TOPICS = [
    {
        slug: "i-need-a-website",
        number: "01",
        question: "I need a website.",
        shortAnswer:
            "Yes, you probably do — but it doesn't need to be big. A simple, well-written one-page website is enough to look real, be found, and start winning customers. Ignore anyone who tells you it needs to be complicated.",
        whyThisMatters:
            "People check you online before anything else. Most of the time, a customer will Google your name on their phone before picking up the phone. If nothing comes up — or what comes up is a rough Facebook page from 2019 — they quietly move on. A tiny, tidy website tells the world you're real, you're open, and you're worth a look.",
        overCoffee:
            "Imagine you own a bakery. Someone hears about your cinnamon scrolls from a friend. They grab their phone and search for you. Nothing comes up. They don't call you. They call the bakery that did show up. That's what a missing website costs — one customer at a time, quietly, forever.",
        letsFixIt: [
            {
                heading: "You need three things",
                body: "A name, a home, and a page. That's it. You don't need a designer, a developer, or a big budget.",
            },
            {
                heading: "1. The name (your domain)",
                body: "This is your address on the internet — yourbusiness.co.nz or yourbusiness.com.au. About $20 a year from Cloudflare, GoDaddy or Squarespace. Buy the domain that matches your business name, exactly. If it's taken, add your city (fernandbone-auckland.co.nz).",
            },
            {
                heading: "2. The home (your website builder)",
                body: "Pick a builder that does the heavy lifting so you can focus on running the business. Squarespace or Wix (about $20/month) are the friendliest. If you're truly on a shoestring, Google gives every small business a free one-page website through Google Business Profile — it's not fancy, but it works.",
            },
            {
                heading: "3. The page (one page, done well)",
                body: "You do not need a menu of ten pages. One page with five things is enough: who you are, what you sell, where you are, how to book or buy, and one real photo (not a stock image of a woman laughing at a salad).",
            },
        ],
        doThisToday: {
            title: "Register your business name as a domain — today.",
            body: "Even if you don't build the site for another month. Domains get taken and there's no getting them back. It's $20, it takes five minutes, and it's yours forever. Go to Cloudflare, type in your business name, and hit buy.",
            timebox: "5 minutes",
        },
        simpleSummary: [
            "Yes, you need a website — but a small one.",
            "People will always Google you before contacting you.",
            "A one-page site with the essentials beats a fancy site you never finish.",
            "Buy the domain today, before someone else does.",
        ],
        whatsNext: {
            teaser: "You've got a website. Now — how do you actually get people to visit it?",
        },
        flowchart: [
            "Someone hears about your business",
            "They Google you on their phone",
            "They find your website",
            "They see what you offer & how to book",
            "They contact you",
            "You get a new customer",
        ],
        checklist: [
            "Pick a domain name that matches your business (yourbusiness.co.nz / .com.au)",
            "Register the domain — about $20/year (Cloudflare or GoDaddy)",
            "Pick a website builder — Squarespace, Wix, or the free Google Business one",
            "Write one page: who, what, where, how to book, one real photo",
            "Add your address so Google Maps knows where you are",
            "Share the link — in your email signature, on Instagram, on your business card",
        ],
    },

    {
        slug: "i-need-more-customers",
        number: "02",
        question: "I need more customers.",
        shortAnswer:
            "More customers doesn't come from shouting louder — it comes from being findable when people need what you sell, and being trusted when they find you. Fancy marketing comes later. The basics come first, and most of them are free.",
        whyThisMatters:
            "A quiet month feels like the sky is falling. But most small businesses aren't short of demand — they're short of visibility. Right now, people who need exactly what you do are searching for it. Some of them will find you. Most won't. Being findable is the whole game.",
        overCoffee:
            "Picture a plumber. Great work, fair prices, been at it for twenty years. His phone rings — but only from the same three builders he's known forever. Meanwhile, on the same street, homeowners typed \"plumber near me\" into Google last week and hired someone else. He didn't need more skill. He needed to show up in that search.",
        letsFixIt: [
            {
                heading: "Fix your Google presence first",
                body: "Your free Google Business Profile is the single biggest lever a small business has. It's what puts you on the map when someone Googles your service near their suburb. Free, quick, and most of your competitors haven't bothered.",
            },
            {
                heading: "Ask your five happiest customers for a review",
                body: "Not a hundred. Five. Text them the Google review link this week. Reviews are what turn a listing into a booking — people trust strangers on the internet more than they trust your website.",
            },
            {
                heading: "Post something on your one channel, once a week",
                body: "Not five channels once a year. Pick one — Instagram, Facebook, LinkedIn, whatever your customers actually use — and show up on the same day every week. Little and often beats big and sporadic.",
            },
            {
                heading: "Make it dead easy to contact you",
                body: "Pick one way to be contacted — phone, WhatsApp, or DM — and put it everywhere: your website header, your Instagram bio, your Google listing, your email signature. Don't make people hunt for how to give you money.",
            },
        ],
        doThisToday: {
            title: "Claim (or update) your free Google Business Profile.",
            body: "Go to google.com/business and search your business name. If it's not there, add it. If it is, claim it and fix anything wrong — hours, phone, photos. This one action puts you on the map, literally, for every \"near me\" search in your suburb. Fifteen minutes changes how many people can find you.",
            timebox: "15 minutes",
        },
        simpleSummary: [
            "More customers means being findable when they need you.",
            "Google Business Profile is the biggest free win — nothing beats it.",
            "Reviews build trust faster than any ad ever will.",
            "Pick one contact method and put it everywhere.",
        ],
        whatsNext: {
            teaser: "Being on Google isn't just about a profile — it's about actually showing up in the search results. That's Topic 03.",
        },
        flowchart: [
            "Someone has a problem you can solve",
            'They Google "your service near me"',
            "They see your Google Business Profile at the top",
            "They see 5-star reviews from real people",
            "They call you",
            "You get a new customer",
        ],
        checklist: [
            "Set up (or claim) your free Google Business Profile",
            "Add real photos — inside, outside, product, team",
            "Add your opening hours and phone number",
            "Ask 5 happy customers for a Google review (send them the review link)",
            "Reply to every review — even the good ones",
            "Post one update per week (photo + a sentence is enough)",
        ],
    },

    {
        slug: "i-need-to-appear-on-google",
        number: "03",
        question: "I need to appear on Google.",
        shortAnswer:
            "Two things get you on Google fast: a claimed Google Business Profile (for local searches like \"cafe near me\") and a clear website (for everything else). Do both. It won't cost you a cent unless you choose to run ads later.",
        whyThisMatters:
            "When someone needs a plumber, a dentist, or a wedding florist, they Google it. If you're not on the first page for what you sell in your town, you're invisible. And the first page isn't magic — for local businesses, it's mostly the free Google Business Profile map plus a decent website. Two things. Both free. Most competitors haven't done them.",
        overCoffee:
            "A wedding florist near Christchurch spent five years making the most beautiful arrangements you've ever seen. Bookings came from friends of friends. She'd never claimed her Google Business Profile. When a bride searched \"Christchurch wedding florist,\" seven other florists appeared on the map before her. She wasn't losing to better florists. She was losing to more visible ones.",
        letsFixIt: [
            {
                heading: "Claim your Google Business Profile",
                body: "Head to google.com/business, search your business, and claim it. Google will send a postcard or call to verify you own it. Once verified, you appear on the map and in \"near me\" searches.",
            },
            {
                heading: "Fill every field, not just the obvious ones",
                body: "Hours, address, phone, categories, description, opening photos, product photos, team photos. Google rewards completeness. Empty profiles hide; full ones rise to the top.",
            },
            {
                heading: "Match your business info everywhere",
                body: "Your business name, address and phone number must be identical on your website, Google, and Facebook. \"Ltd\" vs \"Limited\" or \"Street\" vs \"St\" confuses Google — it thinks you're two businesses. Pick one version and use it everywhere.",
            },
            {
                heading: "Write a homepage that mentions what you do + where",
                body: "\"Yoga studio in Wellington CBD.\" \"Landscape gardener serving South Auckland.\" Plain language, in the first sentence. Google reads that and matches you to searchers in your area.",
            },
        ],
        doThisToday: {
            title: "Google your own business name right now.",
            body: "Type your business name into Google on your phone. What appears? If nothing at all — set up Google Business Profile today. If wrong info appears (old phone number, closed hours from lockdown) — claim the listing and fix it. Ten minutes now saves months of invisibility.",
            timebox: "10 minutes",
        },
        simpleSummary: [
            "Google Business Profile puts you on the map — free.",
            "Your website handles everything else.",
            "Business name, address and phone must match everywhere — exactly.",
            "Reviews make you rank higher without any technical work.",
        ],
        whatsNext: {
            teaser: "You're findable — great. But when people find your social feed, what should they actually see there? That's Topic 04.",
        },
        flowchart: [
            "Person needs your service",
            'Person Googles "[service] near [town]"',
            "Google shows a map of nearby businesses",
            "Your business is on the map with photos & reviews",
            "Person clicks yours and calls",
            "You get a new customer",
        ],
        checklist: [
            "Google your own business — see what shows up",
            "Claim or set up your Google Business Profile",
            "Fill every field: hours, address, phone, categories, description",
            "Add 10 real photos (not stock)",
            "Match your name / address / phone on website & Facebook exactly",
            "Ask 3 recent customers for a Google review",
        ],
    },

    {
        slug: "i-dont-know-what-to-post",
        number: "04",
        question: "I don't know what to post.",
        shortAnswer:
            "You don't need to be clever. Show what you do, who does it, and who it's for. That's it. If it feels obvious to you — that's often the exact thing your customer wants to see.",
        whyThisMatters:
            "Most small businesses go quiet on social because they overthink it. They think every post has to be a marketing masterpiece. It doesn't. People follow you to peek behind the scenes — to feel like they know you before they buy from you. Ordinary is exactly what they came for.",
        overCoffee:
            "A furniture maker in Nelson messages me: \"I've got nothing to post.\" He then tells me about the fifteen-hour day he spent hand-carving a table leg for a customer flying in from Melbourne to see the piece. That's the post. He didn't think it was interesting because he lives it. To everyone else scrolling Instagram at 9pm — it's magic.",
        letsFixIt: [
            {
                heading: "Rotate through four kinds of posts",
                body: "You never run out of ideas if you have four buckets to draw from. Pick one each week and you'll never wonder what to post again.",
            },
            {
                heading: "1. Show the work",
                body: "The making, the finished piece, the messy middle. A tradie's van at 6am. A hairdresser mid-colour. A baker pulling loaves out. People love the how, not just the what.",
            },
            {
                heading: "2. Show the person",
                body: "You, your team, your morning coffee, your dog on-site. People buy from people. The face behind the business is often the most-liked post you'll ever make.",
            },
            {
                heading: "3. Show the customer",
                body: "Who bought it, how they're using it, their happy face — always with their permission. Real customers do the selling for you.",
            },
            {
                heading: "4. Show what you know",
                body: "One tip your customer would find useful. \"Never store tomatoes in the fridge.\" \"Book your car service before the school holidays.\" Small, useful, memorable.",
            },
        ],
        doThisToday: {
            title: "Take one photo right now, of anything in your business.",
            body: "Your work bench. The open kitchen. Your team laughing at something. The half-finished project. Post it with one honest sentence about what's going on. No filters, no plan. That's a post. You just did it.",
            timebox: "10 minutes",
        },
        simpleSummary: [
            "What's obvious to you is often interesting to your customer.",
            "Rotate through four buckets: work, person, customer, tip.",
            "One post per week beats five posts one week and nothing for a month.",
            "Real beats polished every time.",
        ],
        whatsNext: {
            teaser: "Now you know what to post. But who exactly are you posting it for? That's Topic 05.",
        },
        flowchart: [
            "Something real happens in your business",
            "You snap a photo on your phone",
            "You caption it with one honest sentence",
            "You post it once a week, same day, same time",
            "People start recognising your face & story",
            "When they need what you sell, they think of you first",
        ],
        checklist: [
            "Pick your one channel (Instagram, Facebook, or LinkedIn)",
            "Pick your posting day + time (e.g. Wednesdays at 11am)",
            "Take 4 photos this week — one from each bucket (work, person, customer, tip)",
            "Write one sentence per photo (no more)",
            "Schedule them for the next 4 weeks",
            "Reply to every DM within 24 hours",
        ],
    },

    {
        slug: "i-dont-know-who-my-customers-are",
        number: "05",
        question: "I don't know who my customers are.",
        shortAnswer:
            "Your customers are the people who already give you money — start there. Look at your last ten sales. Who are they, where do they live, what did they buy, and why? That's your customer. Not a made-up \"target market.\"",
        whyThisMatters:
            "Marketers love to over-complicate this with personas and demographics. But the fastest way to grow is to find more of the customers you already have — not chase strangers. If you know your ten best customers by name, you can find their friends.",
        overCoffee:
            "A café owner in Ponsonby was ready to spend $2,000 on Instagram ads for \"brunch enthusiasts aged 25 to 40.\" I asked her to name her ten best regulars. She could — Sarah the yoga teacher, Marcus the dad with the twins, Kiri who works from the corner table on Tuesdays. Not one of them matched her \"target market.\" Her customers were locals within walking distance who worked from home. We spent $200 on a neighbourhood postcard instead. Filled the café for three weekends.",
        letsFixIt: [
            {
                heading: "List your last 10 real customers",
                body: "By name, if you can. What did they order? What day did they come in? What did they mention in passing? This is your data — no survey needed.",
            },
            {
                heading: "Look for the pattern",
                body: "Do they mostly live locally? Are they mostly parents? Working from home? Small business owners themselves? One thing usually links them — spot that thing.",
            },
            {
                heading: "Ask them how they found you",
                body: "Text three of them. \"Do you mind me asking — how did you first hear about us?\" The answers will surprise you. Some marketing you thought worked doesn't. Some you never noticed does.",
            },
            {
                heading: "Ask what almost stopped them",
                body: "\"What nearly made you not buy?\" is a magic question. Their answer is exactly what's costing you every other lost customer this month.",
            },
        ],
        doThisToday: {
            title: "Text three real customers with one question.",
            body: "\"Hi — quick one: do you mind me asking how you first heard about us? Just trying to understand what's actually working. Thank you!\" Send it to three people whose numbers you have. Their honest answers will reshape everything.",
            timebox: "10 minutes",
        },
        simpleSummary: [
            "Your best customers already exist — study them.",
            "Don't invent target markets — look at your actual sales.",
            "Ask three questions: who, where, why.",
            "Do more of what works. Stop doing what doesn't.",
        ],
        whatsNext: {
            teaser: "Now that you know who your customers are — do you actually need to be on social media to reach them? That's Topic 06.",
        },
        flowchart: [
            "You look at your last 10 real sales",
            "You notice a pattern (who buys, from where, why)",
            "You spot one channel that keeps bringing them in",
            "You spend more time on that one channel",
            "More of the same customer type finds you",
            "Your business grows without guessing",
        ],
        checklist: [
            "List your last 10 customers (real names, real orders)",
            "Note where each one heard about you",
            "Note one thing they have in common",
            'Text 3 of them: "How did you first hear about us?"',
            "Write down their exact words",
            "Do one thing this month for people like them (a post, an offer, an event)",
        ],
    },

    {
        slug: "do-i-really-need-social-media",
        number: "06",
        question: "Do I really need social media?",
        shortAnswer:
            "Not necessarily. If your customers aren't on social media, you don't need to be either. But you do need to be somewhere findable — Google, email, word of mouth, a great website. Social media is one option, not the only one.",
        whyThisMatters:
            "The social-media pressure on small businesses is enormous — and often wasted. A trades business selling to homeowners might do far better on Google Business Profile than on TikTok. Every hour spent on the wrong channel is an hour not spent on the right one. Doing nothing on Instagram isn't laziness. It's focus.",
        overCoffee:
            "A retired accountant took over a farm supply store in the King Country. He was told he \"had to\" be on Instagram. He tried for six months — five followers, all family. Meanwhile, a handwritten sign at the shop door said \"Sold out again — call to reserve.\" That was the whole story. His customers weren't on Instagram. They were farmers who checked the local WhatsApp group at 6am. Different customer, different channel. He deleted the Instagram account and slept better.",
        letsFixIt: [
            {
                heading: "Ask where your customers actually spend time online",
                body: "Not what social media they \"like\" — where they honestly spend time. It might be a group chat, a Facebook group, a Reddit thread, a WhatsApp community, or nothing at all. Follow the honest answer, even if it's boring.",
            },
            {
                heading: "Pick ONE channel — not five",
                body: "One channel done well beats five done badly. Choose the one your customers actually use, and let the others rest.",
            },
            {
                heading: "Match the channel to the customer",
                body: "Customers 40+ tend to hang out on Facebook and read Google reviews. Customers 18-30 lean towards Instagram or TikTok. B2B customers live on LinkedIn or in email newsletters. If yours barely use social at all — put every hour into Google Business Profile and your website instead.",
            },
            {
                heading: "Delete or hide the accounts you'll never use",
                body: "A ghost Instagram account with two posts from 2022 makes you look worse than having no Instagram at all. Delete it or hide it. Free yourself.",
            },
        ],
        doThisToday: {
            title: "Ask five customers where they spend time online.",
            body: "Not \"which social media do you use\" — that's a leading question. Ask: \"Where do you actually spend time online in the evenings?\" Follow the honest answer. If four out of five say Facebook — that's your channel, not Instagram.",
            timebox: "15 minutes",
        },
        simpleSummary: [
            "Social media isn't compulsory.",
            "Pick one channel where your customers actually are.",
            "Being brilliant on one beats being average on five.",
            "Google, email, and word of mouth are still real marketing.",
        ],
        whatsNext: {
            teaser: "Say you have a website. It's live. But no one's enquiring. What now? That's Topic 07.",
        },
        flowchart: [
            "You ask your customers where they spend time online",
            "You pick one channel that matches their answer",
            "You show up on that channel consistently",
            "Your customers see you where they already look",
            "They trust you because they see you often",
            "They contact you when they're ready to buy",
        ],
        checklist: [
            "Ask 5 customers where they spend time online",
            "Circle the channel that shows up most in their answers",
            "Pick that one channel — and no others",
            "Delete or hide the accounts you'll never use",
            "Post once a week on the one you chose",
            "Track: are you getting DMs, comments, enquiries? If yes, keep going",
        ],
    },

    {
        slug: "my-website-isnt-getting-enquiries",
        number: "07",
        question: "My website isn't getting enquiries.",
        shortAnswer:
            "Websites don't get enquiries by existing — they get enquiries by being obvious. Nine times out of ten, the fix is making one thing crystal clear: what you do, who it's for, and the exact next step to contact you. Put that at the top of your homepage. Done.",
        whyThisMatters:
            "Most small business websites hide the important bit. The homepage says \"Welcome to our website!\" instead of \"Cafe in Grey Lynn. Open 7am–3pm. Book a table.\" Visitors bounce in six seconds if they can't figure out what you do and what to do next. It's not your website that's broken. It's the first ten words.",
        overCoffee:
            "A hairdresser in Titirangi paid a friend of a friend $3,000 to build her website. It was beautiful — swirling videos on the homepage, a poem about hair, and a tiny \"contact\" link tucked in the corner. Bookings stayed flat. We replaced her homepage with three lines: her name, \"Hairdresser in Titirangi — over 20 years' experience,\" and a huge \"Book online\" button. Bookings tripled. The website hadn't changed. The clarity had.",
        letsFixIt: [
            {
                heading: "One sentence, at the top",
                body: "What you do + where you are. \"Family dentist in Manukau.\" \"Small-batch coffee roaster, delivered across Auckland.\" \"Wedding photographer for Central Otago.\" Plain, direct, and above everything else.",
            },
            {
                heading: "One obvious button — right below",
                body: "\"Book,\" \"Get a quote,\" \"Call us.\" Big. Coral. Impossible to miss. Not \"contact\" (too vague). Give people one action and make it huge.",
            },
            {
                heading: "One real photo (not a stock image)",
                body: "Your team, your shop, your work. Stock photos of women laughing at salads scream \"we're pretending.\" A real photo, even a phone snap, screams \"we're real.\"",
            },
            {
                heading: "Contact details in the header of every page",
                body: "Phone number in the top-right. Address in the footer. Hours near the top. Never make someone hunt for how to give you money.",
            },
            {
                heading: "The 5-second test",
                body: "Show your homepage to a friend who's never seen it. Ask: \"In 5 seconds, tell me what this business does.\" If they can't — rewrite the top. Repeat until they can.",
            },
        ],
        doThisToday: {
            title: "Write one sentence describing your business — and paste it at the top.",
            body: "Use the plainest words you can find. If a 12-year-old wouldn't understand it, rewrite it. \"Handmade jewellery in Nelson — earrings, rings, custom pieces.\" That's it. Put that above everything on your homepage today.",
            timebox: "15 minutes",
        },
        simpleSummary: [
            "Clarity beats cleverness — every time.",
            "One clear sentence + one obvious button = 90% of the work.",
            "Real photos beat stock photos.",
            "If a stranger can't tell what you do in 5 seconds, rewrite the top.",
        ],
        whatsNext: {
            teaser: "Your website's working. But out of all the marketing you could do — where should you actually start? That's Topic 08.",
        },
        flowchart: [
            "Visitor lands on your homepage",
            "They see one sentence: what you do + where",
            "They see one obvious button: book / contact / call",
            "They click within 10 seconds",
            "They fill in the form or make the call",
            "You get an enquiry",
        ],
        checklist: [
            "Read the first sentence on your homepage — is it clear what you do?",
            "If not — rewrite it in plain words a 12-year-old would understand",
            "Add a big obvious button that says one thing: Book, Call, or Quote",
            "Put your phone number in the top-right corner of every page",
            "Replace one stock photo with one real photo (of you, your team, or your work)",
            "Ask a friend the 5-second test — if they fail, rewrite again",
        ],
    },

    {
        slug: "what-marketing-should-i-do-first",
        number: "08",
        question: "What marketing should I do first?",
        shortAnswer:
            "Get found before you get fancy. Nail three things — a claimed Google Business Profile, a clear one-page website, and a way to ask happy customers for reviews. That's your foundation. Everything else (ads, funnels, TikTok) comes later, when the foundation is holding weight.",
        whyThisMatters:
            "Marketing without a foundation is like pouring water into a bucket with holes. You'll spend money on ads that send people to a confused website. You'll post on TikTok before Google even knows your address. Get the free basics right first — they compound quietly, forever.",
        overCoffee:
            "A brand-new physio in Papatoetoe rang wanting help with Instagram Reels. She had zero patients. I asked to see her website — there wasn't one, just a Facebook page with a stale opening-hours graphic. Google didn't know her. We spent one Saturday building a one-pager, claiming her Google Business Profile, and texting her twelve friends for reviews. Two weeks later — booked out. She never made a Reel.",
        letsFixIt: [
            {
                heading: "The foundation, in order",
                body: "Do these five things before you spend a dollar on marketing. They compound. Each one makes the next one work harder.",
            },
            {
                heading: "1. Claim your Google Business Profile",
                body: "Free. 30 minutes. Biggest single win of all the free stuff. Puts you on the map for every \"near me\" search.",
            },
            {
                heading: "2. Build a clear one-page website",
                body: "One day. See Topic 07 for the exact recipe. Just: what you do, where you are, one photo, one button.",
            },
            {
                heading: "3. Ask 10 happy customers for a Google review",
                body: "20 minutes of texting. Reviews are what turn a listing into a booking. Nothing else builds trust faster.",
            },
            {
                heading: "4. Pick one channel and post weekly",
                body: "See Topic 06. One channel — chosen because your customers are there — updated once a week. That's it.",
            },
            {
                heading: "5. Track one number every month",
                body: "Bookings? Calls? DMs? Sales? Same number, every month. Simple. Otherwise you'll never know what's working.",
            },
        ],
        doThisToday: {
            title: "Write your foundation checklist on paper — and circle the one you don't have.",
            body: "Google Business Profile. Clear website. 10 reviews. One channel, weekly. One number, monthly. Which one is missing? That's your next job. Not every job — just that one.",
            timebox: "10 minutes",
        },
        simpleSummary: [
            "Foundation first, fancy stuff later.",
            "The free basics compound forever.",
            "One channel done well beats five done badly.",
            "Track one number so you know what's actually working.",
        ],
        whatsNext: {
            teaser: "You've got the plan. But how much of your money should you actually spend on all this? That's Topic 09.",
        },
        flowchart: [
            "You set up Google Business Profile",
            "You build a clear one-page website",
            "You get 10 real customer reviews",
            "You pick one channel and post weekly",
            "You track one number monthly",
            "Your foundation holds — ads & campaigns work on top",
        ],
        checklist: [
            "Claim your Google Business Profile",
            "Build (or fix) a clear one-page website",
            "Ask 10 customers for a Google review — send them the link",
            "Pick your one social channel",
            "Set one posting day per week",
            "Track one number: bookings, calls, DMs, or sales — same one every month",
        ],
    },

    {
        slug: "how-much-should-i-spend-on-marketing",
        number: "09",
        question: "How much should I spend on marketing?",
        shortAnswer:
            "For most small businesses starting out, you shouldn't spend a cent until the free basics are done (see Topic 08). Once they are, a common starting range is 3–8% of monthly revenue — but honestly, spend less than that and spend it slowly. Every dollar spent before the foundation is done is money out the window.",
        whyThisMatters:
            "The marketing industry has convinced every small business they need to spend more. The truth is smaller and calmer: small businesses grow by getting the free things right, then adding a tiny bit of paid spend once they know what's actually working. Rushed spending is where budgets die.",
        overCoffee:
            "A pilates studio owner rang me in tears. She'd spent $4,500 on a \"marketing package\" from a Facebook agency in three months. Not one new client. When we looked, her website had the wrong opening hours, no booking button, and Google didn't know she'd moved suburbs. The problem wasn't ad budget. She'd paid thousands to send strangers to a broken shop. The same money, spent on fixing the shop first, would have filled her classes twice over.",
        letsFixIt: [
            {
                heading: "Free basics before paid spend — every time",
                body: "Topic 08 is not optional. It's the wall your ad money bounces off if it isn't done. Finish the foundation before spending a cent.",
            },
            {
                heading: "Start tiny — $50 to $200 total",
                body: "Not a monthly budget. A total experiment. Run it for two weeks. Watch what happens. This teaches you what works before you scale.",
            },
            {
                heading: "Only spend on what you can track",
                body: "If you can't see whether it worked, don't do it. Google Ads and Meta Ads let you see clicks, calls, bookings. \"Brand awareness\" campaigns often don't. Skip them.",
            },
            {
                heading: "Never sign a 12-month agency contract too early",
                body: "Not until you've spent a year building your foundation and testing tiny experiments yourself. Agencies are amplifiers, not saviours. Amplify a broken business and you break it faster.",
            },
            {
                heading: "The rule of thumb (once you're ready)",
                body: "3–8% of monthly revenue is a common starting range for a small business that's past the foundation stage. Below 3% and you're probably not investing enough to grow. Above 8% and you're probably wasting money you could keep.",
            },
        ],
        doThisToday: {
            title: "Add up what you've spent on marketing in the last 3 months — and be brutally honest about the results.",
            body: "Every subscription, every ad, every agency invoice. Write them down. Next to each one, write the actual result you can point to. If you can't match a dollar to a result, circle it. Pause the circled ones next month. Redirect that money into the foundation instead.",
            timebox: "20 minutes",
        },
        simpleSummary: [
            "Free basics before paid spend — always.",
            "Start tiny ($50–$200 for 2 weeks) before scaling anything.",
            "Only spend on things you can measure.",
            "Don't sign long agency contracts until you know what's working.",
        ],
        whatsNext: {
            teaser: "You've got the foundation and you're spending sensibly. Business is steady. What comes next — how do you actually grow? Topic 10.",
        },
        flowchart: [
            "You finish the free foundation",
            "You track one number every month",
            "You start with a tiny paid experiment ($50–$200)",
            "You measure what happened",
            "If it worked, do more. If not, try something else",
            "Your spend grows only when your results grow",
        ],
        checklist: [
            "Confirm all Topic 08 foundation items are done",
            "Add up marketing spend from the last 3 months",
            "Write down the actual result from each dollar (honestly)",
            "Set a starting monthly budget you can live without",
            "Run one tiny experiment ($50–$200, 2 weeks) and watch closely",
            "Never spend more than you can afford to lose in a month",
        ],
    },

    {
        slug: "im-ready-to-grow-what-now",
        number: "10",
        question: "I'm ready to grow — what now?",
        shortAnswer:
            "Congratulations — that's a real place to be. Growth is different work from starting up. The next moves are: double down on what's already working, stop doing the three things that aren't, and get help before you burn out. Growing is about focus, not adding.",
        whyThisMatters:
            "Most small businesses stall at the \"ready to grow\" moment because they add — new products, new channels, new services — instead of subtracting. Growth is often unglamorous: cutting the offers that don't sell, hiring the one person who takes the busywork off you, saying no to the shiny distraction. That's where the next level lives.",
        overCoffee:
            "A landscaping team in Hamilton was booked six weeks out and ready to double the crew. The owner asked me what new services he should add. I asked him what services already made most of the money. Turns out one job type — retaining walls — was making 70% of his revenue and 90% of his profit. We stopped promoting the other five. Six months later: three trucks, no more late nights, everyone paid better. He didn't add. He focused.",
        letsFixIt: [
            {
                heading: "List everything you sell — then find the two that pay the bills",
                body: "Every service, every product, every offer. Circle the top two by revenue. Circle the top two by profit. If they match, that's your business now. Consider quietly retiring the rest.",
            },
            {
                heading: "Find more of your best customers",
                body: "See Topic 05. Your happiest, best-paying customers all share something. Find that thing. Do more of the marketing that brings them in.",
            },
            {
                heading: "Take one thing off your own plate",
                body: "Bookkeeping, invoicing, DMs, admin — hire a real person, even 4 hours a month. The energy you get back pays for itself twice over.",
            },
            {
                heading: "Automate the boring bits",
                body: "Cheap tools now handle invoicing, bookings, follow-up emails, review requests. Set them up once and they earn while you sleep.",
            },
            {
                heading: "Say no to something this month",
                body: "A distraction, a project, a client that drains you. Growing is what you cut, not what you add. Saying no is a growth skill.",
            },
        ],
        doThisToday: {
            title: "Pick one weekly 30-minute task you hate — and price what you'd pay to remove it.",
            body: "Bookkeeping, social replies, invoicing, admin — whatever it is. Post the task on Facebook Marketplace or a local job board today. You don't have to hire yet. Just see what comes back. It'll shift how you think about your time forever.",
            timebox: "20 minutes",
        },
        simpleSummary: [
            "Growth is subtraction, not addition.",
            "Double down on what already works — quietly drop the rest.",
            "Get help before you burn out — even 4 hours a month.",
            "Automate the boring bits.",
            "Say no to the shiny distraction.",
        ],
        whatsNext: {
            teaser: "You've made it to the end of the playbook. If you've done even half of what's in here — you know more than most small business owners about marketing. Now go do the work. And come back any time you get stuck.",
        },
        flowchart: [
            "You look at what actually made you money last year",
            "You double down on those 2 things",
            "You quietly drop the rest",
            "You hire someone to take one task off your plate",
            "You automate the boring bits",
            "You grow with less stress, not more",
        ],
        checklist: [
            "List every product / service you sell",
            "Circle the top 2 that pay the bills",
            "Note the ones you'll quietly retire",
            "Identify one weekly task you'd pay to remove",
            "Post the task on Facebook Marketplace or a local job board",
            "Set up one automation (invoicing, booking, or email replies)",
        ],
    },
];

export function findTopic(slug) {
    return TOPICS.find((t) => t.slug === slug);
}

export function neighbours(slug) {
    const idx = TOPICS.findIndex((t) => t.slug === slug);
    if (idx === -1) return { prev: null, next: null };
    return {
        prev: idx > 0 ? TOPICS[idx - 1] : null,
        next: idx < TOPICS.length - 1 ? TOPICS[idx + 1] : null,
    };
}

export function isDrafted(topic) {
    return Boolean(topic && topic.shortAnswer);
}
